import { useEffect, useRef, useState } from "react";
import { isDeepEqual } from "../utilities/helpers";

function useLocalStorage<T>(
    key: string,
    initialValue: T,
): [value: T, setValue: (value: React.SetStateAction<T>) => void] {
    function getInitialValue() {
        const item = window.localStorage.getItem(key);

        try {
            return item ? JSON.parse(item) : initialValue;
        } catch {
            return initialValue;
        }
    }

    const [storedValue, setStoredValue] = useState<T>(getInitialValue);
    const isFirstRender = useRef(true);

    // Update if key changes
    useEffect(() => {
        // No need to update on the very first render as the state will be set in the `useState` hook
        if (isFirstRender.current) {
            return;
        }

        const initial = getInitialValue();
        if (!isDeepEqual(storedValue, initial)) {
            setStoredValue(initial);
        }

        isFirstRender.current = false;
    }, [key]);

    function setValue(setStateAction: React.SetStateAction<T>) {
        // Allow value to be a function so we have same API as useState
        const valueToStore = setStateAction instanceof Function ? setStateAction(storedValue) : setStateAction;

        setStoredValue(valueToStore);
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
    }

    return [storedValue, setValue];
}

export default useLocalStorage;
